/* eslint-disable @applitools/compat/no-async-await */

import { boot, createEraroError, eraroCodesMap } from '@dazn/bootstrap-lib';

import { getBrandName } from '../brandModule/brandUtils';

const CACHE_KEY = 'startup_static_cache';

function deepMerge(
    obj1: Record<string, unknown>,
    obj2: Record<string, unknown>,
): boot.startupData.StartupData {
    const result: Record<string, unknown> = { ...obj1 };

    for (const key in obj2) {
        if (Array.isArray(obj2[key]) && Array.isArray(obj1[key])) {
            result[key] = [
                ...(obj1[key] as unknown[]),
                ...(obj2[key] as unknown[]),
            ];
        } else if (
            obj2[key] &&
            typeof obj2[key] === 'object' &&
            !Array.isArray(obj2[key]) &&
            obj1[key] &&
            typeof obj1[key] === 'object' &&
            !Array.isArray(obj1[key])
        ) {
            result[key] = deepMerge(
                obj1[key] as Record<string, unknown>,
                obj2[key] as Record<string, unknown>,
            );
        } else {
            result[key] = obj2[key];
        }
    }

    return result as unknown as boot.startupData.StartupData;
}

const ENVIRONMENT_URLS: Record<
    boot.config.Environment,
    { base: string; cache: string }
> = {
    production: {
        base: 'https://startup.core.indazn.com',
        cache: 'https://startup.core.indazn.com',
    },
    staging: {
        base: 'https://startup.core.dazn-stage.com',
        cache: 'https://startup.core.dazn-stage.com',
    },
    edge: {
        base: 'https://startup.core.dazn-stage.com',
        cache: 'https://startup.core.dazn-stage.com',
    },
    testing: {
        base: 'https://startup.core.dazn-stage.com',
        cache: 'https://startup.core.dazn-stage.com',
    },
    development: {
        base: 'https://startup.core.dazn-stage.com',
        cache: 'https://startup.core.dazn-stage.com',
    },
};

const fetchStartupResponse = async (
    env: boot.config.Environment,
    shouldUseMarcoPolo: boolean,
    guid: string,
    sessionId: string,
    customHeaders?: {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    },
    languages?: string,
): Promise<boot.startupData.StartupData> => {
    const storedStaticDataString = localStorage.getItem(CACHE_KEY);
    let storedStaticData:
        | (Partial<boot.startupData.StartupData> & { StaticTimestamp?: string })
        | null = null;

    // Parse stored static data if available
    if (storedStaticDataString) {
        try {
            storedStaticData = JSON.parse(storedStaticDataString);
        } catch (error) {
            console.error('Failed to parse data from localStorage:', error);
        }
    }
    const brand = getBrandName();

    try {
        const headers: HeadersInit = {};

        if (shouldUseMarcoPolo) {
            headers['x-dazn-device'] = guid;
            headers['x-dazn-marco'] = 'true';
            headers['x-session-id'] = sessionId;
        }

        // Add custom common headers if enabled
        if (customHeaders?.isCustomHeadersEnabled) {
            if (customHeaders.brandName) {
                headers['X-BRAND'] = customHeaders.brandName;
            }
            if (customHeaders.daznId) {
                headers['X-DAZNID'] = customHeaders.daznId;
            }
        }

        // Fetch main data
        const mainResponse = await fetch(
            `${
                ENVIRONMENT_URLS[env].base
            }/v1/main/web?Platform=web&LandingPageKey=generic${
                languages ? `&Languages=${languages}` : ''
            }${brand ? `&Brand=${brand}` : ''}`,
            {
                headers,
            },
        );
        if (!mainResponse.ok) {
            throw new Error(
                `Failed to fetch main data: ${mainResponse.statusText}`,
            );
        }
        const mainData = await mainResponse.json();

        // Check static data validity
        if (
            !storedStaticData ||
            storedStaticData.StaticTimestamp !== mainData.StaticTimestamp
        ) {
            const staticResponse = await fetch(
                `${ENVIRONMENT_URLS[env].cache}/v1/static/web${
                    brand ? `?Brand=${brand}` : ''
                }`,
            );
            if (!staticResponse.ok) {
                throw new Error(
                    `Failed to fetch static data: ${staticResponse.statusText}`,
                );
            }
            const staticData = await staticResponse.json();

            // Update localStorage with the new static data
            localStorage.setItem(CACHE_KEY, JSON.stringify(staticData));

            return deepMerge(staticData, mainData);
        }

        // Return combined data
        return deepMerge(storedStaticData, mainData);
    } catch (error) {
        console.error('Error fetching startup response:', error);
        throw error;
    }
};

/**
 *  @see [Request](../../docs/api-call.md)
 */
export const fetchStartupData = async (
    environment: boot.config.Environment,
    shouldUseMarcoPolo: boolean,
    guid: string,
    sessionId: string,
    customHeaders?: {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    },
    languages?: string,
): Promise<boot.startupData.StartupData> => {
    try {
        const data = await fetchStartupResponse(
            environment,
            shouldUseMarcoPolo,
            guid,
            sessionId,
            customHeaders,
            languages,
        );
        return data;
    } catch (error) {
        throw createEraroError(
            `Failed to get startup data. ${String(error)}`,
            eraroCodesMap.category.StartupService,
            eraroCodesMap.code.AppLoadError,
            eraroCodesMap.internal.ServerError,
            error,
        );
    }
};
